import { useCallback, useEffect, useMemo, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { ghostBtnClass, inputClass } from "./logisticsRoutesShared";
import {
  listLogisticsShipments,
  splitLogisticsShipment,
  type LogiShipment,
} from "@/lib/logistics/shipmentsClient";
import type { MfgUnit } from "@/lib/manufacturing/types";
import { unitHandoffOptionLabel } from "@/lib/ops/handoffIdentity";

export function LogisticsShipmentsStrip(props: {
  units: MfgUnit[];
  enabled: boolean;
  navTick: number;
  onChanged: () => void;
}) {
  const [shipments, setShipments] = useState<LogiShipment[]>([]);
  const [unitId, setUnitId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);

  const reload = useCallback(async () => {
    const res = await listLogisticsShipments();
    if (!res.ok) {
      setShipments([]);
      return;
    }
    setShipments(res.data.shipments);
    setMissing(res.data.missing_table);
  }, []);

  useEffect(() => {
    if (props.enabled) void reload();
  }, [props.enabled, props.navTick, reload]);

  const byId = useMemo(() => new Map(shipments.map((s) => [s.id, s])), [shipments]);

  async function onSplit() {
    if (!unitId) return;
    setBusy(true);
    setError(null);
    const res = await splitLogisticsShipment(unitId, reason);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setReason("");
    await reload();
    props.onChanged();
  }

  if (!props.enabled) return null;

  return (
    <SectionCard
      title="Shipment loads"
      description="Plant load letter stays on the unit. Logistics can break a finished, ready building into AC-L1 so the rest of AC ships later."
    >
      {missing ? (
        <p className="text-xs text-amber-800 dark:text-amber-200">
          Apply docs/sql/logi_shipments_v1.sql to mint and split loads.
        </p>
      ) : null}
      {error ? <p className="mb-2 text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {shipments.length === 0 && !missing ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">
          No logistics shipments yet. Dispatch a production load to mint the original code.
        </p>
      ) : (
        <ul className="mb-3 text-sm space-y-1">
          {shipments.map((s) => (
            <li key={s.id} className="flex flex-wrap gap-2">
              <span className="font-mono font-semibold">{s.code}</span>
              {s.parent_shipment_id ? (
                <span className="text-xs text-gray-500">
                  split from {byId.get(s.parent_shipment_id)?.code ?? s.origin_code}
                  {s.split_reason ? ` — ${s.split_reason}` : ""}
                </span>
              ) : (
                <span className="text-xs text-gray-500">origin {s.origin_code}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-gray-600 dark:text-zinc-300">
          Break out unit
          <select
            className={`${inputClass} mt-1`}
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
          >
            <option value="">(finished + ready)</option>
            {props.units.map((u) => (
              <option key={u.id} value={u.id}>
                {unitHandoffOptionLabel(u)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-gray-600 dark:text-zinc-300">
          Reason
          <input
            className={`${inputClass} mt-1`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Customer ready / finished early"
          />
        </label>
        <button type="button" className={ghostBtnClass} disabled={busy || !unitId} onClick={() => void onSplit()}>
          Split to next -L
        </button>
      </div>
    </SectionCard>
  );
}
